import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, MessageSquare } from "lucide-react";
import { saveEmail, savePhoneNumber, n8nSaveEmail, n8nSavePhone, n8nSaveEmailSS, n8nSavePhoneSS, n8nSavePhoneSlickText } from "@/lib/firebase";
import { validateEmail, validatePhone } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import logoWhite from "@/assets/logo-white.svg";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";

interface HotelPackagesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HotelPackagesModal = ({ isOpen, onClose }: HotelPackagesModalProps) => {
  const [step, setStep] = useState<"email" | "phone" | "done">("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (!canSubmit("email")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("email")}s before submitting again.`);
      return;
    }
    const validationError = validateEmail(email);
    if (validationError) { setErrorMessage(validationError); return; }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        saveEmail(email, "hotel-packages"),
        n8nSaveEmail(email, "hotel-packages").catch(console.error),
        n8nSaveEmailSS(email, "hotel-packages").catch(console.error),
      ]);
      recordSubmission("email");
      setSuccessMessage("Email saved!");
      
      setTimeout(() => {
        setStep("phone");
        setSuccessMessage(null);
      }, 800);
    } catch (error) {
      console.error("Error saving email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    if (!canSubmit("phone")) {
      setErrorMessage(`Please wait ${getSecondsRemaining("phone")}s before submitting again.`);
      return;
    }
    const validationError = validatePhone(phone);
    if (validationError) { setErrorMessage(validationError); return; }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await Promise.all([
        savePhoneNumber(phone, "hotel-packages"),
        n8nSavePhone(phone, "hotel-packages").catch(console.error),
        n8nSavePhoneSS(phone, "hotel-packages").catch(console.error),
        n8nSavePhoneSlickText(phone).catch(console.error),
      ]);
      recordSubmission("phone");
      setSuccessMessage("You're on the list!");
      setStep("done");
    } catch (error) {
      console.error("Error saving phone:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state after closing
    setTimeout(() => {
      setStep("email");
      setEmail("");
      setPhone("");
      setSuccessMessage(null);
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-md bg-yacht-blue rounded-3xl overflow-hidden shadow-2xl"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background graphic */}
              <img
                src={boatsGraphic}
                alt=""
                className="absolute -bottom-20 -right-20 w-[300px] opacity-10 pointer-events-none"
                style={{ filter: "brightness(0) invert(1)" }}
              />

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-white/30 hover:text-white/60 transition-colors z-20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="relative z-10 p-8 pt-10">
                {/* Logo */}
                <div className="flex justify-center mb-4">
                  <img src={logoWhite} alt="Yacht Rock Detroit" className="h-16" />
                </div>

                {/* Tag */}
                <div className="flex justify-center mb-4">
                  <span className="px-4 py-1.5 bg-yacht-yellow/20 text-yacht-yellow text-xs font-medium tracking-[0.2em] uppercase rounded-full">
                    Hotel Packages
                  </span>
                </div>

                {step === "done" ? (
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                      You're In! 🎉
                    </h2>
                    <p className="text-white/70 mb-6">
                      We'll let you know when hotel deals are available.
                    </p>
                    <button
                      onClick={handleClose}
                      className="px-8 py-3 bg-yacht-yellow text-yacht-charcoal font-bold rounded-full hover:scale-105 transition-transform"
                    >
                      Sounds Good
                    </button>
                  </motion.div>
                ) : (
                  <>
                    {/* Headline */}
                    <div className="text-center mb-6">
                      <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                        Hotel Package Updates
                      </h2>
                      <p className="text-white/70">
                        {step === "email" 
                          ? "Be first to know when hotel deals are available." 
                          : "Add your number for instant text updates."}
                      </p>
                    </div>

                    {/* Form */}
                    {step === "email" ? (
                      <motion.form
                        onSubmit={handleEmailSubmit}
                        className="space-y-4"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        key="email-form"
                      >
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                          <input
                            type="email"
                            id="hotel-email"
                            name="email"
                            autoComplete="email"
                            inputMode="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-yellow text-lg"
                          />
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50"
                        >
                          {isSubmitting ? "Saving..." : "Keep Me Posted"}
                        </button>

                        {errorMessage && (
                          <p className="text-red-400 text-center text-sm font-medium">{errorMessage}</p>
                        )}
                        {successMessage && (
                          <p className="text-green-400 text-center font-medium">{successMessage}</p>
                        )}

                        <button
                          type="button"
                          onClick={() => setStep("phone")}
                          className="w-full text-white/50 hover:text-white text-sm underline transition-colors"
                        >
                          I'd rather sign up via SMS
                        </button>
                      </motion.form>
                    ) : (
                      <motion.form
                        onSubmit={handlePhoneSubmit}
                        className="space-y-4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        key="phone-form"
                      >
                        <div className="relative">
                          <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yacht-charcoal/40" />
                          <input
                            type="tel"
                            id="hotel-phone"
                            name="phone"
                            autoComplete="tel"
                            inputMode="tel"
                            placeholder="(555) 123-4567"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:ring-2 focus:ring-yacht-yellow text-lg"
                          />
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50"
                        >
                          {isSubmitting ? "Saving..." : "Complete Signup"}
                        </button>

                        {successMessage && (
                          <p className="text-green-400 text-center font-medium">{successMessage}</p>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setStep("email");
                            setSuccessMessage(null);
                          }}
                          className="w-full text-white/50 hover:text-white text-sm underline transition-colors"
                        >
                          ← Back to email
                        </button>
                      </motion.form>
                    )}
                  </>
                )}
              </div>

              {/* Bottom accent */}
              <div className="h-2 bg-yacht-yellow" />
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HotelPackagesModal;
